/**
 * The Combine — gamified, sports-centric, endless AI practice (Tier 1 GameDay Skill Engine).
 * Real-time streaks & accolades ("He's on fire!"), coach hints, encouragement on misses,
 * and Scholar Dollars that feed the school-side Mastery Radar.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Loader2, ArrowLeft, ArrowRight, Lightbulb, Trophy, Zap,
  Target, CheckCircle2, XCircle, RefreshCw, Dumbbell, BookOpen, FlaskConical, Users, Calendar,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SUBJECTS = [
  { key: 'math', title: 'Math / Data Analytics', icon: Target, color: '#3B82F6', blurb: 'Stats, ratios, geometry — through the game.' },
  { key: 'ela', title: 'ELA / Persuasion', icon: BookOpen, color: '#8B5CF6', blurb: 'Reading, writing & brand voice like a pro.' },
  { key: 'science', title: 'Science / Biomechanics', icon: FlaskConical, color: '#10B981', blurb: 'The physics & biology of peak performance.' },
  { key: 'social_studies', title: 'Social Studies / Leadership', icon: Users, color: '#F59E0B', blurb: 'History, civics & captain-level decisions.' },
];

const CORRECT_CALLS = ['Nothing but net!', 'Boomshakalaka!', 'Bucket!', 'That\'s a dime!', 'Clean rep!', 'Textbook!'];
const MISS_CALLS = ['Shake it off — next possession.', 'Reset and attack again.', 'Champions bounce back. Run it.', 'Watch the coach hint, then counter.', 'No panic. Next rep wins it.'];
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

const fireText = (streak) => {
  if (streak >= 6) return 'UNCONSCIOUS! Get this scholar out of the gym!';
  if (streak >= 5) return 'RAZZLE DAZZLE! He literally cannot miss!';
  if (streak >= 3) return "HE'S ON FIRE! Can't miss right now!";
  if (streak === 2) return 'Heating up...';
  return '';
};

// Coach cues + athlete/thought-leader quotes shown while a fresh set generates.
const COACH_CUES = [
  'Coach is drawing up the game plan…',
  'Chalking the board…',
  'Setting up your reps…',
  'Loading today\'s film…',
  'Studying the defense…',
  'Taping up — get your mind right…',
  'Building your workout…',
];
const PREP_QUOTES = [
  ['You miss 100% of the shots you don\'t take.', 'Wayne Gretzky'],
  ['Hard work beats talent when talent doesn\'t work hard.', 'Tim Notke'],
  ['Champions keep playing until they get it right.', 'Billie Jean King'],
  ['It\'s not whether you get knocked down; it\'s whether you get up.', 'Vince Lombardi'],
  ['The will to prepare to win is what matters most.', 'Bear Bryant'],
  ['Discipline is doing it like you love it, even when you hate it.', 'Mike Tyson'],
  ['Don\'t count the days — make the days count.', 'Muhammad Ali'],
  ['Make each day your masterpiece.', 'John Wooden'],
  ['Concentration and mental toughness are the margins of victory.', 'Bill Russell'],
  ['Push yourself again and again until the final buzzer.', 'Larry Bird'],
];

const CombineLoader = () => {
  const [mix] = useState(() => {
    // Interleave a few coach cues and prep quotes, randomized per load.
    const cues = [...COACH_CUES].sort(() => Math.random() - 0.5).slice(0, 4).map((t) => ({ type: 'cue', text: t }));
    const quotes = [...PREP_QUOTES].sort(() => Math.random() - 0.5).slice(0, 4).map(([t, a]) => ({ type: 'quote', text: t, author: a }));
    const out = [];
    for (let i = 0; i < 4; i++) { out.push(cues[i]); out.push(quotes[i]); }
    return out;
  });
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % mix.length), 2600);
    return () => clearInterval(t);
  }, [mix.length]);
  const cur = mix[i];
  return (
    <div className="min-h-screen bg-[#08080a] flex items-center justify-center text-white px-6" data-testid="combine-loader">
      <div className="text-center max-w-lg">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-6 text-orange-500" />
        {cur?.type === 'quote' ? (
          <div key={i} className="animate-in fade-in duration-500">
            <p className="text-lg sm:text-xl font-medium italic leading-relaxed text-white">&ldquo;{cur.text}&rdquo;</p>
            <p className="mt-3 text-xs font-black uppercase tracking-[0.25em] text-orange-400">— {cur.author}</p>
          </div>
        ) : (
          <p key={i} className="animate-in fade-in duration-500 text-zinc-400 uppercase tracking-[0.3em] text-xs">{cur?.text}</p>
        )}
        <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-zinc-600">Building your custom set…</p>
      </div>
    </div>
  );
};


export default function CombinePractice() {
  const nav = useNavigate();
  const [phase, setPhase] = useState('select'); // select | loading | play | done
  const [mode, setMode] = useState('subject');
  const [subject, setSubject] = useState(null);
  const [subjectTitle, setSubjectTitle] = useState('');
  // Sport IQ mini-picker
  const [siqOpen, setSiqOpen] = useState(false);
  const [siqSport, setSiqSport] = useState('basketball');
  const [siqCategories, setSiqCategories] = useState([]);
  const [siqCategory, setSiqCategory] = useState('');
  const [siqPlayerType, setSiqPlayerType] = useState('any');
  // Auto-adaptive difficulty ladder — remembered per sport across sessions
  const TIERS = [
    { key: 'foundational', label: 'Foundational', blurb: 'Core reads & definitions' },
    { key: 'intermediate', label: 'Intermediate', blurb: 'Combined situational reads' },
    { key: 'advanced', label: 'Advanced', blurb: 'Counter-reads & sequencing' },
    { key: 'elite', label: 'Elite', blurb: 'Late-clock & pro-level IQ' },
  ];
  const [siqDifficultyBySport, setSiqDifficultyBySport] = useState({});
  const [siqAttainedDifficulty, setSiqAttainedDifficulty] = useState(null);
  const [grade, setGrade] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null); // {correct, answer_index, explanation}
  const [showHint, setShowHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [accolade, setAccolade] = useState('');
  const [summary, setSummary] = useState(null);
  const [averages, setAverages] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [stateLabel, setStateLabel] = useState('');

  const fire = streak >= 3;

  const loadHistory = useCallback(() => {
    api.get('/academy/combine/history', { headers: auth() })
      .then((r) => setAverages(r.data.averages || [])).catch(() => {});
    api.get('/academy/combine/assignments', { headers: auth() })
      .then((r) => setAssignments(r.data.assignments || [])).catch(() => {});
    api.get('/academy/combine/sport-iq/preferences', { headers: auth() })
      .then((r) => setSiqDifficultyBySport(r.data.difficulty_by_sport || {}))
      .catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Sport IQ — load available categories whenever sport/player-type filter changes
  useEffect(() => {
    if (!siqOpen) return;
    const params = new URLSearchParams();
    if (siqPlayerType && siqPlayerType !== 'any') params.append('player_type', siqPlayerType);
    api.get(`/academy/${siqSport}-iq/glossary${params.toString() ? `?${params}` : ''}`)
      .then((r) => {
        const cats = (r.data.categories || []).filter((c) => c.count > 0);
        setSiqCategories(cats);
        if (siqCategory && !cats.find((c) => c.key === siqCategory)) setSiqCategory('');
      })
      .catch(() => setSiqCategories([]));
  }, [siqOpen, siqSport, siqPlayerType, siqCategory]);

  const run = async ({ m, subj, sport, category, playerType, difficulty }) => {
    setPhase('loading'); setMode(m);
    setSubject(subj ? subj.key : null);
    try {
      const payload = { mode: m, subject: subj ? subj.key : null };
      if (m === 'sport_iq') {
        payload.sport_iq_sport = sport || 'basketball';
        if (category) payload.sport_iq_category = category;
        if (playerType && playerType !== 'any') payload.sport_iq_player_type = playerType;
        if (difficulty) payload.sport_iq_difficulty = difficulty;
      }
      const r = await api.post('/academy/combine/generate', payload, { headers: auth() });
      setSessionId(r.data.session_id);
      setQuestions(r.data.questions || []);
      setGrade(r.data.grade);
      setStateLabel(r.data.state_label || '');
      setSubjectTitle(r.data.title || (subj ? subj.title : 'Practice'));
      if (r.data.sport_iq_difficulty) setSiqAttainedDifficulty(r.data.sport_iq_difficulty);
      setIdx(0); setSelected(null); setResult(null); setShowHint(false);
      setStreak(0); setBestStreak(0); setCorrectCount(0); setAccolade('');
      setPhase('play');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load a practice set. Try again.');
      setPhase('select');
    }
  };

  const q = questions[idx];

  const lockIn = async () => {
    if (selected === null || result) return;
    setBusy(true);
    try {
      const r = await api.post('/academy/combine/check', { session_id: sessionId, qid: q.id, given_index: selected }, { headers: auth() });
      setResult(r.data);
      if (r.data.correct) {
        const ns = streak + 1;
        setStreak(ns); setBestStreak((b) => Math.max(b, ns)); setCorrectCount((c) => c + 1);
        setAccolade(fireText(ns) || rand(CORRECT_CALLS));
      } else {
        if (r.data.streak_protected) {
          toast.success('🧊 Streak Freeze used — your streak is safe!');
          setAccolade('STREAK FREEZE! Your streak survived the miss.');
        } else {
          setStreak(0);
          setAccolade(rand(MISS_CALLS));
        }
      }
    } catch { toast.error('Could not check that answer.'); } finally { setBusy(false); }
  };

  const next = async () => {
    if (idx + 1 < questions.length) {
      setIdx(idx + 1); setSelected(null); setResult(null); setShowHint(false); setAccolade('');
      return;
    }
    // finish -> submit
    setBusy(true);
    try {
      const r = await api.post('/academy/combine/submit', { session_id: sessionId, responses: [], best_streak: bestStreak }, { headers: auth() });
      setSummary(r.data);
      if (r.data.sport_iq_next_difficulty) {
        setSiqAttainedDifficulty(r.data.sport_iq_next_difficulty);
        setSiqDifficultyBySport((prev) => ({ ...prev, [siqSport]: r.data.sport_iq_next_difficulty }));
      }
      setPhase('done');
      loadHistory();
    } catch { toast.error('Could not save your set.'); } finally { setBusy(false); }
  };

  // ---------- SELECT ----------
  if (phase === 'select') {
    return (
      <div className="min-h-screen bg-[#08080a] text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button onClick={() => nav('/academy/school')} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6" data-testid="combine-back">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Dumbbell className="w-7 h-7 text-orange-500" />
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">THE COMBINE</h1>
          </div>
          <p className="text-zinc-400 mb-2">Sports-centric reps to keep you sharp. Endless, grade-aligned, and it counts toward your Scholar Rating.</p>
          <p className="text-[11px] uppercase tracking-[0.3em] text-orange-500/80 font-black mb-6">GameDay Skill Engine · Tier 1</p>

          {/* Coach assignments (homework) */}
          {assignments.length > 0 && (
            <div className="mb-8" data-testid="combine-assignments">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-black mb-3">From Your Coach</div>
              <div className="space-y-2">
                {assignments.map((a) => (
                  <div key={a.id} className={`flex items-center gap-3 border-2 rounded-xl p-3 ${a.done ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-emerald-500/50 bg-emerald-500/10'}`} data-testid={`assignment-${a.id}`}>
                    <Trophy className={`w-5 h-5 flex-shrink-0 ${a.done ? 'text-emerald-500' : 'text-emerald-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black text-white">{a.subject_title}{a.note ? ` — ${a.note}` : ''}</div>
                      <div className="text-[11px] text-zinc-400">Assigned by {a.coach}{a.done ? ' · Completed ✓' : ''}</div>
                    </div>
                    {a.done
                      ? <span className="text-[10px] font-black uppercase text-emerald-400">Done</span>
                      : <button onClick={() => run({ m: a.mode, subj: a.subject ? SUBJECTS.find((s) => s.key === a.subject) : null })} className="bg-emerald-500 hover:bg-emerald-400 text-black px-3 py-2 text-[10px] font-black uppercase tracking-wider rounded" data-testid={`assignment-start-${a.id}`}>Start</button>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Training modes */}
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            <button onClick={() => run({ m: 'all' })} data-testid="combine-mode-all"
              className="group text-left border-2 border-orange-500/50 bg-orange-500/10 rounded-2xl p-4 hover:border-orange-500 transition-colors">
              <Dumbbell className="w-6 h-6 text-orange-500 mb-2" />
              <div className="font-black">All-Around Workout</div>
              <div className="text-xs text-zinc-400 mt-0.5">Mixed reps across all 4 subjects.</div>
            </button>
            <button onClick={() => run({ m: 'today' })} data-testid="combine-mode-today"
              className="group text-left border-2 border-zinc-800 rounded-2xl p-4 hover:border-blue-500/60 bg-black/40 transition-colors">
              <Calendar className="w-6 h-6 text-blue-400 mb-2" />
              <div className="font-black">Today's Lesson</div>
              <div className="text-xs text-zinc-400 mt-0.5">Reps on what the class covered today.</div>
            </button>
            <button onClick={() => run({ m: 'focus' })} data-testid="combine-mode-focus"
              className="group text-left border-2 border-zinc-800 rounded-2xl p-4 hover:border-emerald-500/60 bg-black/40 transition-colors">
              <Target className="w-6 h-6 text-emerald-400 mb-2" />
              <div className="font-black">Target a Weakness</div>
              <div className="text-xs text-zinc-400 mt-0.5">Attacks your lowest subject first.</div>
            </button>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-black mb-3">Or drill a specific subject</div>

          {/* Sport IQ — pull practice questions straight from The IQ of {Sport} course */}
          <div className="border-2 border-orange-500/40 rounded-2xl bg-black/40 mb-4 overflow-hidden" data-testid="siq-card">
            <button
              onClick={() => setSiqOpen((o) => !o)}
              className="w-full text-left p-5 flex items-start gap-4 hover:bg-orange-500/5 transition-colors"
              data-testid="siq-open"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-orange-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-lg font-black text-white">Sport IQ Practice</div>
                <div className="text-sm text-zinc-400 mt-1">
                  Pull 5 questions straight from The IQ of Basketball / Football / Soccer / Track — definitions, when-to-use, when-NOT, scenario reads, and counters.
                </div>
                <div className="mt-2 text-[10px] tracking-widest font-black uppercase text-orange-500">
                  {siqOpen ? 'Configure ↓' : 'Tap to configure →'}
                </div>
              </div>
            </button>

            {siqOpen && (
              <div className="border-t border-orange-500/20 p-5 space-y-4">
                {/* Sport picker */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mb-2">Course</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { key: 'basketball', label: 'Basketball' },
                      { key: 'football', label: 'Football' },
                      { key: 'soccer', label: 'Soccer' },
                      { key: 'track', label: 'Track' },
                    ].map((s) => (
                      <button
                        key={s.key}
                        onClick={() => { setSiqSport(s.key); setSiqCategory(''); }}
                        className={`text-[11px] font-black uppercase tracking-widest px-3 py-2 border-2 rounded-lg ${
                          siqSport === s.key ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                        }`}
                        data-testid={`siq-sport-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Player type picker (basketball only) */}
                {siqSport === 'basketball' && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mb-2">For</div>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { key: 'any', label: 'Any' },
                        { key: 'guard', label: 'Guard' },
                        { key: 'wing', label: 'Wing' },
                        { key: 'big', label: 'Big' },
                      ].map((p) => (
                        <button
                          key={p.key}
                          onClick={() => setSiqPlayerType(p.key)}
                          className={`text-[11px] font-black uppercase tracking-widest px-3 py-2 border-2 rounded-lg ${
                            siqPlayerType === p.key ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                          }`}
                          data-testid={`siq-ptype-${p.key}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category (optional) */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mb-2">
                    Focus (optional)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSiqCategory('')}
                      className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 rounded ${
                        !siqCategory ? 'bg-white text-black border-white' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                      }`}
                      data-testid="siq-cat-all"
                    >
                      All Terms
                    </button>
                    {siqCategories.map((c) => (
                      <button
                        key={c.key}
                        onClick={() => setSiqCategory(c.key === siqCategory ? '' : c.key)}
                        className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 rounded ${
                          siqCategory === c.key ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                        }`}
                        data-testid={`siq-cat-${c.key}`}
                      >
                        {c.label} ({c.count})
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => run({
                    m: 'sport_iq',
                    sport: siqSport,
                    category: siqCategory,
                    playerType: siqPlayerType,
                    difficulty: siqDifficultyBySport[siqSport] || undefined,
                  })}
                  className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em] rounded-lg flex items-center justify-center gap-2"
                  data-testid="siq-run"
                >
                  <Target className="w-4 h-4" /> Start 5-Rep Sport IQ Set
                  {siqDifficultyBySport[siqSport] && (
                    <span className="opacity-70 text-[10px]">· {siqDifficultyBySport[siqSport].toUpperCase()}</span>
                  )}
                </button>

                {/* Auto-Adaptive Difficulty Ladder */}
                <div className="border-t border-orange-500/20 pt-4 mt-2" data-testid="siq-ladder">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Your Tier</div>
                    <div className="text-[9px] uppercase tracking-widest font-black text-orange-500/80">Auto-adjusts</div>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {TIERS.map((t) => {
                      const active = (siqDifficultyBySport[siqSport] || 'foundational') === t.key;
                      return (
                        <button
                          key={t.key}
                          onClick={() => setSiqDifficultyBySport((prev) => ({ ...prev, [siqSport]: t.key }))}
                          className={`text-[9px] font-black uppercase tracking-widest px-2 py-2 border-2 rounded-lg leading-tight ${
                            active ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'
                          }`}
                          data-testid={`siq-tier-${t.key}`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                    ≥ 80% correct promotes you a tier · ≤ 40% drops you back down · we remember where you left off.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {SUBJECTS.map((s) => (
              <button key={s.key} onClick={() => run({ m: 'subject', subj: s })} data-testid={`combine-subject-${s.key}`}
                className="group text-left border-2 border-zinc-800 rounded-2xl p-5 hover:border-orange-500/60 transition-colors bg-black/40 relative overflow-hidden">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${s.color}22` }}>
                  <s.icon className="w-6 h-6" style={{ color: s.color }} />
                </div>
                <div className="text-lg font-black">{s.title}</div>
                <div className="text-sm text-zinc-500 mt-1">{s.blurb}</div>
                {averages.find((a) => a.subject === s.key) && (
                  <div className="mt-3 text-[11px] text-zinc-400">
                    Avg <span className="font-black text-white">{averages.find((a) => a.subject === s.key).avg}%</span>
                    <span className="text-zinc-600"> · {averages.find((a) => a.subject === s.key).sets} sets</span>
                  </div>
                )}
                <ArrowRight className="w-5 h-5 text-zinc-700 group-hover:text-orange-500 absolute top-5 right-5 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- LOADING ----------
  if (phase === 'loading') {
    return <CombineLoader />;
  }

  // ---------- DONE ----------
  if (phase === 'done' && summary) {
    const perfect = summary.correct === summary.total;
    return (
      <div className="min-h-screen bg-[#08080a] text-white flex items-center justify-center px-4" data-testid="combine-done">
        <div className="max-w-md w-full text-center">
          <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-5 ${perfect ? 'bg-orange-500/20' : 'bg-blue-500/20'}`}>
            {perfect ? <Flame className="w-10 h-10 text-orange-500" /> : <Trophy className="w-10 h-10 text-blue-400" />}
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-black">Final Whistle · {subjectTitle}</div>
          <div className="text-6xl font-black my-3">{summary.score}%</div>
          <div className="text-zinc-400 mb-6">{summary.correct} of {summary.total} buckets · best streak {bestStreak}</div>
          <div className="flex items-center justify-center gap-2 mb-4 text-orange-400 font-black">
            <Zap className="w-5 h-5" /> +{summary.xp_awarded} Scholar Dollars {summary.xp_awarded === 0 && <span className="text-zinc-600 text-xs font-normal">(daily Scholar Dollars already banked — reps still count!)</span>}
          </div>
          {mode === 'sport_iq' && summary.sport_iq_next_difficulty && (
            <div className="mb-6 border-2 border-orange-500/40 bg-orange-500/10 rounded-xl px-4 py-3" data-testid="combine-siq-next-tier">
              <div className="text-[9px] uppercase tracking-[0.3em] font-black text-orange-500/80 mb-1">Your Next Tier</div>
              <div className="text-lg font-black text-orange-300">
                {summary.sport_iq_next_difficulty.toUpperCase()}
              </div>
              <div className="text-[11px] text-zinc-400 mt-1">
                We'll pull your next set at this difficulty. Beat it to move up.
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => run({ m: mode, subj: subject ? SUBJECTS.find((s) => s.key === subject) : null })} className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-black py-3.5 font-black text-xs uppercase tracking-widest rounded-lg" data-testid="combine-run-back">
              <RefreshCw className="w-4 h-4" /> Run It Back
            </button>
            <button onClick={() => setPhase('select')} className="border-2 border-zinc-800 hover:border-zinc-600 py-3.5 font-black text-xs uppercase tracking-widest rounded-lg" data-testid="combine-switch">
              Switch Subject
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- PLAY ----------
  const pct = ((idx) / questions.length) * 100;
  return (
    <div className={`min-h-screen text-white transition-colors duration-500 ${fire ? 'bg-gradient-to-b from-orange-950/40 to-[#08080a]' : 'bg-[#08080a]'}`} data-testid="combine-play">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* top bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-black">{subjectTitle} · G{grade}{stateLabel ? ` · ${stateLabel}` : ''}</div>
          <div className={`flex items-center gap-1.5 font-black text-sm ${fire ? 'text-orange-400' : 'text-zinc-500'}`} data-testid="combine-streak">
            {[0, 1, 2].map((i) => <Flame key={i} className={`w-4 h-4 ${streak > i ? 'text-orange-500' : 'text-zinc-700'} ${fire ? 'animate-pulse' : ''}`} />)}
            <span className="ml-1">{streak} streak</span>
          </div>
        </div>
        {/* progress */}
        <div className="h-1.5 bg-zinc-900 rounded-full mb-2 overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] text-zinc-600 mb-5">Rep {idx + 1} of {questions.length}</div>

        {/* ON FIRE banner */}
        {fire && (
          <div className="mb-4 flex items-center gap-2 bg-orange-500/15 border border-orange-500/40 rounded-xl px-4 py-2.5 animate-pulse" data-testid="combine-onfire">
            <Flame className="w-5 h-5 text-orange-500" />
            <span className="font-black text-orange-300 tracking-wide text-sm">{fireText(streak)}</span>
          </div>
        )}

        {q && (
          <div className="border-2 border-zinc-800 rounded-2xl p-5 bg-black/40">
            {q.standard_code && <div className="text-[10px] font-mono text-zinc-600 mb-2">CA {q.standard_code} · {q.standard_text}</div>}
            <p className="text-lg font-semibold leading-relaxed mb-5" data-testid="combine-question">{q.question}</p>

            <div className="space-y-2.5">
              {q.choices.map((c, i) => {
                let cls = 'border-zinc-800 hover:border-zinc-600';
                if (result) {
                  if (i === result.answer_index) cls = 'border-emerald-500 bg-emerald-500/10';
                  else if (i === selected) cls = 'border-red-500 bg-red-500/10';
                  else cls = 'border-zinc-800 opacity-50';
                } else if (i === selected) cls = 'border-orange-500 bg-orange-500/10';
                return (
                  <button key={i} disabled={!!result} onClick={() => setSelected(i)} data-testid={`combine-choice-${i}`}
                    className={`w-full text-left border-2 rounded-xl px-4 py-3 text-sm transition-colors flex items-center gap-3 ${cls}`}>
                    <span className="w-6 h-6 rounded-full border border-current flex items-center justify-center text-[11px] font-black flex-shrink-0">{String.fromCharCode(65 + i)}</span>
                    <span className="flex-1">{c}</span>
                    {result && i === result.answer_index && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
                    {result && !result.correct && i === selected && <XCircle className="w-5 h-5 text-red-400" />}
                  </button>
                );
              })}
            </div>

            {/* coach hint */}
            {!result && (
              <button onClick={() => setShowHint((s) => !s)} className="mt-4 flex items-center gap-2 text-xs text-amber-400/90 hover:text-amber-300 font-bold" data-testid="combine-hint-toggle">
                <Lightbulb className="w-4 h-4" /> {showHint ? 'Hide' : 'Coach'} hint
              </button>
            )}
            {showHint && !result && <div className="mt-2 text-sm text-amber-200/80 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3" data-testid="combine-hint">{q.coach_hint}</div>}

            {/* feedback */}
            {result && (
              <div className={`mt-4 rounded-xl p-4 ${result.correct ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`} data-testid="combine-feedback">
                <div className={`flex items-center gap-2 font-black text-sm ${result.correct ? 'text-emerald-300' : 'text-red-300'}`}>
                  {result.correct ? <Flame className="w-5 h-5 text-orange-500" /> : <Target className="w-5 h-5" />}
                  {accolade}
                </div>
                <p className="text-sm text-zinc-300 mt-2">{result.explanation}</p>
              </div>
            )}

            {/* action */}
            <div className="mt-5">
              {!result ? (
                <button onClick={lockIn} disabled={selected === null || busy}
                  className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black py-3.5 font-black text-xs uppercase tracking-[0.2em] rounded-lg flex items-center justify-center gap-2"
                  data-testid="combine-lock-in">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />} Lock It In
                </button>
              ) : (
                <button onClick={next} disabled={busy}
                  className="w-full bg-white hover:bg-zinc-200 text-black py-3.5 font-black text-xs uppercase tracking-[0.2em] rounded-lg flex items-center justify-center gap-2"
                  data-testid="combine-next">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{idx + 1 < questions.length ? 'Next Rep' : 'Finish Set'} <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
